import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, subDays, addDays } from 'date-fns';
import api from '../api/client';
import { useFincaId } from '../context/FincaContext';
import { usePermisos } from '../context/PermisosContext';
import ParametrosConfig from './ParametrosConfig';
import CompararParametros from './CompararParametros';

export default function Parametros() {
  const fincaIdContext = useFincaId();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tienePermiso } = usePermisos();

  const tabParam = searchParams.get('tab');
  const [vista, setVista] = useState(
    tabParam === 'grafica' ? 'grafica' : tabParam === 'comparar' ? 'comparar' : 'historial'
  );

  useEffect(() => {
    const nuevoTab = searchParams.get('tab');
    if (nuevoTab === 'grafica') setVista('grafica');
    else if (nuevoTab === 'comparar') setVista('comparar');
    else setVista('historial');
  }, [searchParams]);

  const [parametros, setParametros] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [piscinas, setPiscinas] = useState([]);
  const [config, setConfig] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [sectorId, setSectorId] = useState('');
  const [piscinaId, setPiscinaId] = useState('');
  const [verOxigeno, setVerOxigeno] = useState(true);
  const [verTemperatura, setVerTemperatura] = useState(true);
  const [filtrosExpandidos, setFiltrosExpandidos] = useState(true);
  const [mostrarConfig, setMostrarConfig] = useState(false);

  useEffect(() => {
    if (!fincaIdContext) return;
    api.get(`/sectores/finca/${fincaIdContext}`)
      .then(({ data }) => setSectores(Array.isArray(data) ? data : []))
      .catch(() => {});
    api.get('/parametros/config')
      .then(({ data }) => setConfig(data))
      .catch(() => {});
  }, [fincaIdContext]);

  useEffect(() => {
    if (!sectorId) {
      setPiscinas([]);
      setPiscinaId('');
      return;
    }
    api.get(`/parametros/piscinas-con-datos?sector_id=${sectorId}`)
      .then(({ data }) => setPiscinas(Array.isArray(data) ? data : []))
      .catch(() => setPiscinas([]));
  }, [sectorId]);

  // ✅ FUNCIÓN PARA OBTENER EL YYYY-MM-DD EN HORA LOCAL (Evita desfases)
  const getLocalDateString = (fechaStr) => {
    if (!fechaStr) return '';
    // Forzamos la 'Z' para que JS sepa que es UTC y lo convierta a la hora local del navegador
    const strConZ = fechaStr.includes('Z') || fechaStr.includes('+') ? fechaStr : `${fechaStr}Z`;
    const d = new Date(strConZ);
    if (isNaN(d.getTime())) return fechaStr.substring(0, 10);
    
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const fetchParametros = useCallback(async () => {
    setCargando(true);
    setError(null);
    setParametros([]);

    try {
      const params = { limit: 2000 }; // Aumentamos el límite por seguridad
      
      // ✅ ENVIAMOS UN RANGO SÚPER AMPLIO AL BACKEND (5 días antes y 5 días después)
      // Esto garantiza que la base de datos SIEMPRE envíe los registros, 
      // sin importar si los guardó con 1 o 2 días de desfase.
      if (desde) {
        const fechaDesde = subDays(new Date(`${desde}T12:00:00`), 5);
        params.desde = format(fechaDesde, "yyyy-MM-dd'T'00:00:00");
      }
      if (hasta) {
        const fechaHasta = addDays(new Date(`${hasta}T12:00:00`), 5);
        params.hasta = format(fechaHasta, "yyyy-MM-dd'T'23:59:59");
      }
      
      if (piscinaId) params.piscina_id = piscinaId;
      else if (sectorId) params.sector_id = sectorId;

      const { data } = await api.get('/parametros', { params });
      
      // ✅ FILTRO EXACTO EN EL FRONTEND
      // Como el backend nos trajo TODO (por el rango amplio), ahora sí filtramos exacto
      // usando la fecha local convertida, garantizando que del 08/10 al 08/10 salgan los 4 del 8.
      let dataFiltrada = data;
      if (desde || hasta) {
        dataFiltrada = data.filter(p => {
          const fechaLocal = getLocalDateString(p.fecha_hora);
          if (desde && fechaLocal < desde) return false;
          if (hasta && fechaLocal > hasta) return false;
          return true;
        });
      }

      setParametros(Array.isArray(dataFiltrada) ? dataFiltrada : []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || e.message);
    } finally {
      setCargando(false);
    }
  }, [desde, hasta, piscinaId, sectorId]);

  useEffect(() => {
    fetchParametros();
  }, [fetchParametros]);

  const datosGrafica = useMemo(() => {
    return [...parametros]
      .sort((a, b) => new Date(a.fecha_hora) - new Date(b.fecha_hora))
      .map((p) => ({
        fecha: format(new Date(p.fecha_hora), 'dd/MM HH:mm'),
        oxigeno: p.oxigeno_mg_l != null ? Number(p.oxigeno_mg_l) : null,
        temperatura: p.temperatura_c != null ? Number(p.temperatura_c) : null,
        piscina: p.piscina_codigo,
      }));
  }, [parametros]);

  const kpis = useMemo(() => {
    if (parametros.length === 0) return { total: 0, criticos: 0, barbeos: 0 };
    let criticos = 0, barbeos = 0;
    const criticoMax = config?.oxigeno_critico_max ?? 2.0;
    const barbeoMax = config?.oxigeno_barbeo_max ?? 2.2;
    for (const p of parametros) {
      const ox = p.oxigeno_mg_l != null ? Number(p.oxigeno_mg_l) : null;
      if (ox != null) {
        if (ox < criticoMax) criticos++;
        else if (ox < barbeoMax) barbeos++;
      }
    }
    return { total: parametros.length, criticos, barbeos };
  }, [parametros, config]);

  const estadoOxigeno = (valor) => {
    if (valor == null || !config) return null;
    const v = Number(valor);
    const critico = Number(config.oxigeno_critico_max);
    const barbeo = Number(config.oxigeno_barbeo_max);
    const recuperando = Number(config.oxigeno_recuperando_max);
    const estable = Number(config.oxigeno_estable_max);

    if (v < critico) return { texto: 'CRÍTICO', color: 'bg-red-100 text-red-800' };
    if (v < barbeo) return { texto: 'POSIBLE BAJA', color: 'bg-orange-100 text-orange-800' };
    if (v < recuperando) return { texto: 'RECUPERANDO', color: 'bg-amber-100 text-amber-800' };
    if (v < estable) return { texto: 'ESTABLE', color: 'bg-lime-100 text-lime-800' };
    return { texto: 'ÓPTIMO', color: 'bg-green-100 text-green-800' };
  };

  const limpiarFiltros = () => {
    setDesde(''); setHasta('');
    setSectorId(''); setPiscinaId('');
    setSearchParams({});
  };

  const resumenFiltros = () => {
    const partes = [];
    partes.push(vista === 'historial' ? 'Historial' : vista === 'grafica' ? 'Gráfica' : 'Comparar');
    partes.push(sectorId ? `Sector` : 'Todos los sectores');
    if (piscinaId) partes.push('Piscina');
    if (verOxigeno && vista === 'grafica') partes.push('O₂');
    if (verTemperatura && vista === 'grafica') partes.push('T°');
    if (desde) partes.push(`desde ${format(new Date(`${desde}T12:00:00`), 'dd/MM/yy')}`);
    if (hasta) partes.push(`hasta ${format(new Date(`${hasta}T12:00:00`), 'dd/MM/yy')}`);
    return partes.join(' · ');
  };

  return (
    <div className="p-4 md:p-6 flex flex-col h-full w-full min-h-0 bg-gray-100">
      
      {/* ─── Filtros ─── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4 shrink-0">
        {!filtrosExpandidos && (
          <div className="flex items-center gap-3 px-4 py-2 border-b">
            <button
              onClick={() => setFiltrosExpandidos(true)}
              className="text-cyan-700 text-xs font-medium flex items-center gap-1"
            >
              🔽 Mostrar filtros
            </button>
            <span className="text-xs text-gray-500 truncate">
              {resumenFiltros()}
            </span>
          </div>
        )}

        {filtrosExpandidos && (
          <div className="px-4 py-3">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide">Filtros</h3>
              <button
                onClick={() => setFiltrosExpandidos(false)}
                className="text-cyan-700 text-xs font-medium"
              >
                🔼 Ocultar
              </button>
            </div>

            <div className="flex flex-wrap items-end justify-center gap-4">
              
              {vista === 'grafica' && (
                <div className="flex gap-4 pb-2">
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer text-gray-700">
                    <input
                      type="checkbox"
                      checked={verOxigeno}
                      onChange={(e) => setVerOxigeno(e.target.checked)}
                      className="w-3.5 h-3.5"
                    />
                    <span className="inline-block w-2.5 h-2.5 bg-cyan-600 rounded-sm"></span>
                    Oxígeno
                  </label>
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer text-gray-700">
                    <input
                      type="checkbox"
                      checked={verTemperatura}
                      onChange={(e) => setVerTemperatura(e.target.checked)}
                      className="w-3.5 h-3.5"
                    />
                    <span className="inline-block w-2.5 h-2.5 bg-orange-500 rounded-sm"></span>
                    Temperatura
                  </label>
                </div>
              )}

              {vista !== 'comparar' && (
                <>
                  <label className="block">
                    <span className="text-[10px] font-medium text-gray-500 uppercase">Desde</span>
                    <input
                      type="date"
                      value={desde}
                      onChange={(e) => setDesde(e.target.value)}
                      className="mt-0.5 w-[130px] px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-cyan-500"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-medium text-gray-500 uppercase">Hasta</span>
                    <input
                      type="date"
                      value={hasta}
                      onChange={(e) => setHasta(e.target.value)}
                      className="mt-0.5 w-[130px] px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-cyan-500"
                    />
                  </label>
                </>
              )}

              <div className="flex items-end gap-3">
                <label className="block">
                  <span className="text-[10px] font-medium text-gray-500 uppercase">Sector:</span>
                  <select
                    value={sectorId}
                    onChange={(e) => {
                      setSectorId(e.target.value);
                      setPiscinaId('');
                    }}
                    className="mt-0.5 w-[160px] px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-cyan-500"
                  >
                    <option value="">Todos</option>
                    {sectores.map((s) => (
                      <option key={s.id} value={s.id}>{s.nombre}</option>
                    ))}
                  </select>
                </label>

                <div className="text-gray-300 text-lg pb-1">|</div>

                <label className="block">
                  <span className="text-[10px] font-medium text-gray-500 uppercase">Piscina:</span>
                  <select
                    value={piscinaId}
                    onChange={(e) => setPiscinaId(e.target.value)}
                    disabled={!sectorId}
                    className="mt-0.5 w-[180px] px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-cyan-500 disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    {!sectorId ? (
                      <option value="">Selecciona un sector primero</option>
                    ) : (
                      <>
                        <option value="">{vista === 'comparar' ? '— Selecciona piscina —' : 'Todas las piscinas'}</option>
                        {piscinas.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.codigo}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </label>
              </div>

              {vista !== 'comparar' && (
                <div className="flex items-center gap-2 ml-2 pb-0.5">
                  <button
                    onClick={limpiarFiltros}
                    className="px-3 py-1 border rounded text-xs hover:bg-gray-100 h-[26px]"
                  >
                    Limpiar
                  </button>
                  {tienePermiso('configuracion.piscinas') && (
                    <button
                      onClick={() => setMostrarConfig(true)}
                      className="px-3 py-1 border border-cyan-700 text-cyan-700 rounded text-xs hover:bg-cyan-50 h-[26px]"
                      title="Configurar umbrales"
                    >
                      ⚙️ Umbrales
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ─── Contenido con scroll interno para no cortarse ─── */}
      <div className="flex-1 min-h-0 overflow-y-auto relative z-0 pr-1 pb-4">
        {vista === 'comparar' ? (
          <CompararParametros piscinas={piscinas} piscinaSeleccionada={piscinaId} />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="text-xs text-gray-500">Total registros</div>
                <div className="text-2xl font-bold text-gray-800 mt-1">{kpis.total}</div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 border-l-4 border-l-red-500">
                <div className="text-xs text-gray-500">Registros críticos</div>
                <div className="text-2xl font-bold text-red-600 mt-1">{kpis.criticos}</div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 border-l-4 border-l-orange-500">
                <div className="text-xs text-gray-500">Posibles bajas</div>
                <div className="text-2xl font-bold text-orange-600 mt-1">{kpis.barbeos}</div>
              </div>
            </div>

            {error && (
              <div className="bg-red-100 text-red-800 p-3 rounded mb-4">{error}</div>
            )}

            {cargando ? (
              <div className="bg-white p-6 rounded shadow text-center text-gray-500">
                Cargando...
              </div>
            ) : parametros.length === 0 ? (
              <div className="bg-white p-6 rounded shadow text-center text-gray-500">
                No hay parámetros en este filtro.
              </div>
            ) : vista === 'historial' ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600">Fecha</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600">Sector</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600">Piscina</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600">Técnico</th>
                        <th className="text-right px-3 py-2 font-semibold text-gray-600">O₂ (mg/L)</th>
                        <th className="text-right px-3 py-2 font-semibold text-gray-600">T° (°C)</th>
                        <th className="text-center px-3 py-2 font-semibold text-gray-600">Estado</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600">Observaciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parametros.map((p) => {
                        const ox = p.oxigeno_mg_l != null ? Number(p.oxigeno_mg_l) : null;
                        const estado = estadoOxigeno(ox);
                        return (
                          <tr key={p.id} className="border-t hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {format(new Date(p.fecha_hora), 'dd/MM/yyyy HH:mm')}
                            </td>
                            <td className="px-3 py-2">{p.sector_nombre}</td>
                            <td className="px-3 py-2 font-medium">{p.piscina_codigo}</td>
                            <td className="px-3 py-2 text-gray-600">{p.usuario_nombre}</td>
                            <td className="px-3 py-2 text-right font-mono">
                              {ox != null ? ox.toFixed(2) : '—'}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {p.temperatura_c != null ? Number(p.temperatura_c).toFixed(1) : '—'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {estado ? (
                                <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${estado.color}`}>
                                  {estado.texto}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-3 py-2 text-gray-500 text-xs max-w-xs truncate" title={p.observaciones}>
                              {p.observaciones || '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div style={{ width: '100%', height: 420 }}>
                  <ResponsiveContainer>
                    <LineChart data={datosGrafica} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="fecha" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      {verOxigeno && (
                        <Line type="monotone" dataKey="oxigeno" name="Oxígeno (mg/L)" stroke="#00838F" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                      )}
                      {verTemperatura && (
                        <Line type="monotone" dataKey="temperatura" name="Temperatura (°C)" stroke="#F97316" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Modal de configuración ─── */}
      {mostrarConfig && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{
            backgroundColor: 'rgba(75, 85, 99, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          onClick={() => setMostrarConfig(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            style={{
              border: '2px solid #00838F',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="px-5 py-3 text-white flex items-center justify-between"
              style={{
                background: 'linear-gradient(to right, #00838F, #00ACC1)',
                borderBottom: '2px solid #00838F',
              }}
            >
              <div>
                <h2 className="text-sm font-bold">⚙️ Configurar umbrales</h2>
                <p className="text-xs" style={{ color: '#B2EBF2' }}>
                  Ajusta los límites para clasificar oxígeno y temperatura
                </p>
              </div>
              <button
                onClick={() => setMostrarConfig(false)}
                className="rounded-full w-7 h-7 flex items-center justify-center transition text-xs"
                style={{
                  color: 'rgba(255,255,255,0.9)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 bg-gray-50">
              <ParametrosConfig
                embebido
                onCerrar={() => setMostrarConfig(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}